/**
 * useSessionState — drop-in useState replacement that persists value to
 * sessionStorage, scoped by a caller-supplied key. Persists across
 * react-router navigation (unmount/remount) for the lifetime of the
 * browser tab. Reloads will also restore (a bonus, not the goal).
 *
 * Keys should be namespaced per-project (e.g. `scopeBuilder:<projectId>`)
 * so different projects don't pollute each other. Pass an unstable key
 * like `__no_project__` for the no-project case.
 */
import { useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';

/**
 * Read a JSON-serialized value from sessionStorage.
 *
 * Returns undefined on miss or parse failure so the caller can fall back
 * to its initializer.
 */
function readFromSession<T>(key: string): T | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return undefined;
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

/**
 * Write a JSON-serialized value to sessionStorage. Silently swallows
 * quota/serialization errors so persistence never breaks render flow.
 */
function writeToSession<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Reason: sessionStorage may be full, blocked (private mode), or the
    // value may contain a non-serializable thing like a File. Falling
    // back to in-memory state is the right behavior.
  }
}

/**
 * sessionStorage-backed useState. Reads once at mount, writes on every
 * change. Switching `key` (e.g. when projectId changes) re-reads from the
 * new slot and resets to the initializer if that slot is empty.
 *
 * Args:
 *   key (string): The sessionStorage key. Pass null/empty to disable
 *     persistence entirely (falls back to plain useState behavior).
 *   initialValue (T | () => T): The default value when no persisted
 *     value exists. Matches useState's API.
 *
 * Returns:
 *   [T, Dispatch<SetStateAction<T>>]: Same signature as useState.
 */
export function useSessionState<T>(
  key: string | null,
  initialValue: T | (() => T),
): [T, Dispatch<SetStateAction<T>>] {
  const initialiserRef = useRef(initialValue);

  const [state, setState] = useState<T>(() => {
    if (key) {
      const stored = readFromSession<T>(key);
      if (stored !== undefined) return stored;
    }
    return typeof initialiserRef.current === 'function'
      ? (initialiserRef.current as () => T)()
      : (initialiserRef.current as T);
  });

  // Reason: When the key changes (e.g. user navigates to a different
  // project), pull from the new slot. If empty, reset to initial value
  // so we don't leak the previous project's state.
  const lastKeyRef = useRef(key);
  useEffect(() => {
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;
    if (key) {
      const stored = readFromSession<T>(key);
      if (stored !== undefined) {
        setState(stored);
        return;
      }
    }
    setState(
      typeof initialiserRef.current === 'function'
        ? (initialiserRef.current as () => T)()
        : (initialiserRef.current as T),
    );
  }, [key]);

  useEffect(() => {
    if (!key) return;
    writeToSession(key, state);
  }, [key, state]);

  return [state, setState];
}

export default useSessionState;
